/**
 * Tiny markdown renderer covering the subset used by KB articles and any other
 * long-form copy on the site:
 *
 *   - `## Heading` and `### Heading` (only level 2 / 3)
 *   - Paragraphs separated by blank lines
 *   - Unordered `- item` lists and ordered `1. item` lists
 *   - **bold**, *italic*, `code`
 *   - [text](url) links — internal links route via React Router; external open in new tab
 *
 * Everything is plain DOM — no `dangerouslySetInnerHTML` — so XSS-safe.
 */
import { Link } from "react-router-dom";

function inline(text, keyPrefix = "i") {
    // Order matters: links → bold → italic → code, all non-greedy.
    const tokens = [];
    let cursor = 0;
    let key = 0;
    const re = /(\[([^\]]+)\]\(([^)]+)\))|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)/g;
    let m;
    while ((m = re.exec(text)) !== null) {
        if (m.index > cursor) tokens.push(text.slice(cursor, m.index));
        if (m[1]) {
            const label = m[2];
            const href = m[3];
            tokens.push(
                /^https?:/.test(href) ? (
                    <a key={`${keyPrefix}-${key++}`} href={href} target="_blank" rel="noopener noreferrer" className="underline">{label}</a>
                ) : (
                    <Link key={`${keyPrefix}-${key++}`} to={href} className="underline">{label}</Link>
                ),
            );
        } else if (m[4]) {
            tokens.push(<strong key={`${keyPrefix}-${key++}`}>{m[5]}</strong>);
        } else if (m[6]) {
            tokens.push(<em key={`${keyPrefix}-${key++}`}>{m[7]}</em>);
        } else if (m[8]) {
            tokens.push(
                <code key={`${keyPrefix}-${key++}`} className="font-mono text-xs bg-ink-100 px-1 py-0.5 border border-ink-200">
                    {m[9]}
                </code>,
            );
        }
        cursor = m.index + m[0].length;
    }
    if (cursor < text.length) tokens.push(text.slice(cursor));
    return tokens;
}

export function Markdown({ source = "", className = "" }) {
    const lines = source.replace(/\r\n/g, "\n").split("\n");
    const blocks = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];

        if (!line.trim()) { i++; continue; }

        if (line.startsWith("### ")) {
            blocks.push(<h3 key={`h3-${i}`} className="text-base font-semibold mt-6 mb-2">{inline(line.slice(4), `h3-${i}`)}</h3>);
            i++; continue;
        }
        if (line.startsWith("## ")) {
            blocks.push(<h2 key={`h2-${i}`} className="text-lg font-semibold mt-8 mb-3">{inline(line.slice(3), `h2-${i}`)}</h2>);
            i++; continue;
        }
        if (line.startsWith("# ")) {
            blocks.push(<h2 key={`h1-${i}`} className="text-xl font-semibold mt-8 mb-3">{inline(line.slice(2), `h1-${i}`)}</h2>);
            i++; continue;
        }

        // unordered list
        if (/^\s*-\s+/.test(line)) {
            const items = [];
            while (i < lines.length && /^\s*-\s+/.test(lines[i])) {
                items.push(lines[i].replace(/^\s*-\s+/, ""));
                i++;
            }
            blocks.push(
                <ul key={`ul-${i}`} className="list-disc pl-5 space-y-1 my-3">
                    {items.map((it, k) => <li key={k}>{inline(it, `ul-${i}-${k}`)}</li>)}
                </ul>,
            );
            continue;
        }

        // ordered list
        if (/^\s*\d+\.\s+/.test(line)) {
            const items = [];
            while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
                items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
                i++;
            }
            blocks.push(
                <ol key={`ol-${i}`} className="list-decimal pl-5 space-y-1 my-3">
                    {items.map((it, k) => <li key={k}>{inline(it, `ol-${i}-${k}`)}</li>)}
                </ol>,
            );
            continue;
        }

        // paragraph: take consecutive non-blank, non-special lines
        const paraLines = [line];
        i++;
        while (
            i < lines.length
            && lines[i].trim()
            && !lines[i].startsWith("#")
            && !/^\s*-\s+/.test(lines[i])
            && !/^\s*\d+\.\s+/.test(lines[i])
        ) {
            paraLines.push(lines[i]);
            i++;
        }
        blocks.push(
            <p key={`p-${i}`} className="leading-relaxed my-3 text-ink-700">
                {inline(paraLines.join(" "), `p-${i}`)}
            </p>,
        );
    }

    return <div className={className} data-testid="markdown">{blocks}</div>;
}
